import { PaymentBatchesSortField, PaymentsSortDirection } from "#graphql/supergraphEnums";
import { PaymentBatchesFilterInput } from "#graphql/supergraphTypes";
import { DATE_FORMATS, ONE_DAY_MS, toStandardFormat } from "#utils/datetime_utils";
import { MoneyType } from "../../../Money";
import { BatchStatusForUI, parseBatchFilter } from "./batchStatusUtil";
import { parseStatusFilter, PaymentStatusForUI } from "./paymentStatusUtils";

type DateFilterType = {
  dateFrom: string;
  dateTo: string;
  type: string;
};

type NumberFilterType = {
  filter: number;
  filterTo: number;
  type: string;
};

type StatusFilterType = {
  values: Array<BatchStatusForUI> | Array<PaymentStatusForUI>;
};

export type PaymentBatchFilterModel = {
  paymentDate?: DateFilterType;
  batchStatus?: StatusFilterType;
  paymentStatus?: StatusFilterType;
  clientName?: string;
  entityName?: string;
  receivingAccount?: string[];
  paymentAmount?: NumberFilterType;
};

export type PaymentBatchSortModel = { colId: string; sort: string };

/**
 * Applies the filter to the input filter
 * @param filterModel the filter model from the ag-grid, based on the user input
 */
export const applyUserFilter = (
  inputFilter: PaymentBatchesFilterInput,
  filter: PaymentBatchFilterModel,
  amountRef?: MoneyType,
) => {
  if (filter.clientName) {
    inputFilter.arenaIds = [filter.clientName];
  }
  if (filter.entityName) {
    inputFilter.entityIds = [filter.entityName];
  }
  if (filter.receivingAccount) {
    inputFilter.positionIds = filter.receivingAccount.map((account) => account);
  }
  if (filter.paymentDate?.type === "inRange") {
    // Adding one day to the end date to include the end date in the range
    const endDate = new Date(filter.paymentDate.dateTo).getTime() + ONE_DAY_MS;
    inputFilter.scheduledProcessDateRange = [
      filter.paymentDate.dateFrom,
      toStandardFormat(new Date(endDate), DATE_FORMATS["YYYY-MM-DD"].long),
    ];
  }
  if (filter.batchStatus?.values?.length) {
    inputFilter.batchStatus = parseBatchFilter(filter.batchStatus.values as BatchStatusForUI[]);
  }
  if (filter.paymentStatus?.values?.length) {
    inputFilter.paymentStatus = parseStatusFilter(
      filter.paymentStatus.values as PaymentStatusForUI[],
    );
  }
  if (filter.paymentAmount?.type) {
    const multiplier = amountRef ? Math.pow(10, amountRef.precision) : 1;
    const filterAmount = filter.paymentAmount.filter * multiplier;
    switch (filter.paymentAmount.type) {
      case "inRange":
        inputFilter.amountRange = [filterAmount, filter.paymentAmount.filterTo * multiplier];
        break;
      case "lessThan":
        inputFilter.amountRange = [0, filterAmount];
        break;
      case "greaterThan":
        inputFilter.amountRange = [filterAmount, Math.pow(2, 31) - 1];
        break;
    }
  }
};

/**
 * Applies the sorter to the input filter
 * @param inputFilter root variable to sent with the API
 * @param sortModel the sort model from the ag-grid, based on the user input
 */
export const applyUserSort = (
  inputFilter: PaymentBatchesFilterInput,
  sortModel: PaymentBatchSortModel[],
) => {
  if (sortModel.length > 0) {
    let orderByColumn = PaymentBatchesSortField.CreatedDate;
    switch (sortModel[0].colId) {
      case "status":
        orderByColumn = PaymentBatchesSortField.BatchStatus;
        break;
      case "paymentAmount":
        orderByColumn = PaymentBatchesSortField.Amount;
        break;
    }
    let orderByDirection = PaymentsSortDirection.Asc;
    if (sortModel[0].sort === "desc") {
      orderByDirection = PaymentsSortDirection.Desc;
    }
    inputFilter.orderByColumn = orderByColumn;
    inputFilter.orderByDirection = orderByDirection;
  }
};
